/**
 * MetricsDashboardPage — Institution App Persistent Module
 *
 * Rolled-up view of throughput, recoveries, defaults, and pipeline stage
 * distribution. Read-only — does not mutate any workflow state.
 *
 * Data source: GET /api/cases/metrics
 * Refresh: on mount + every 60 seconds (auto-refresh)
 */
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../services/api.js";

// ─── Types ───────────────────────────────────────────────────────────────────
interface MetricsSummary {
  total_active: number;
  total_closed: number;
  total_all: number;
  recovered_last_30d: number;
  stalled: number;
  recovery_rate: number;
}
interface MetricsData {
  summary: MetricsSummary;
  by_stage: Record<string, number>;
  step_distribution: Array<{ step: string; count: number }>;
  generated_at: string;
}

// ─── Stage metadata ───────────────────────────────────────────────────────────
const STAGE_META: Array<{ key: string; label: string; color: string; icon: string }> = [
  { key: "initiation",       label: "Initiation",       color: "#6366f1", icon: "🚀" },
  { key: "auction_approval", label: "Auction Approval", color: "#f59e0b", icon: "⚡" },
  { key: "listing_active",   label: "Listing Active",   color: "#3b82f6", icon: "📋" },
  { key: "bidding_approval", label: "Bidding Approval", color: "#8b5cf6", icon: "✅" },
  { key: "live_bidding",     label: "Live Bidding",     color: "#ec4899", icon: "🔨" },
  { key: "winner_approval",  label: "Winner Approval",  color: "#14b8a6", icon: "🏆" },
  { key: "settlement",       label: "Settlement",       color: "#10b981", icon: "💰" },
];

const STEP_LABELS: Record<string, string> = {
  step_1_initiation: "Case Initiation",
  step_2_field_dispatch: "Field Dispatch",
  step_3_field_verification: "Field Verification",
  step_4_hub_dispatch: "Hub Dispatch",
  step_5_hub_witness: "Hub Witness",
  step_6_hub_processing: "Hub Processing",
  step_7_expert_dispatch: "Expert Dispatch",
  step_8_expert_valuation: "Expert Valuation",
  step_9_cataloging: "Cataloging",
  step_10_auction_approval: "Auction Approval",
  step_11_market_listing: "Market Listing",
  step_12_hub_inspection: "Hub Inspection",
  step_13_bidding_approval: "Bidding Approval",
  step_14_market_bidding: "Live Bidding",
  step_15_market_closed: "Auction Closed",
  step_16_winner_approval: "Winner Approval",
  step_17_winner_posted: "Winner Posted",
  step_18_settlement: "Settlement",
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent, icon }: {
  label: string; value: string | number; sub?: string; accent?: string; icon: string;
}): React.ReactElement {
  return (
    <div style={{
      background: "var(--panel)", border: "1px solid var(--border)",
      borderTop: `4px solid ${accent ?? "var(--brand)"}`,
      borderRadius: "var(--radius)", padding: "20px 22px",
      boxShadow: "var(--shadow)", display: "flex", flexDirection: "column", gap: 6,
    }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <p style={{ margin: 0, fontSize: 28, fontWeight: 900, color: "var(--ink)", lineHeight: 1 }}>{value}</p>
      <p style={{ margin: 0, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: accent ?? "var(--brand)" }}>{label}</p>
      {sub && <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>{sub}</p>}
    </div>
  );
}

function StageBar({ label, count, total, color, icon }: {
  label: string; count: number; total: number; color: string; icon: string;
}): React.ReactElement {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", display: "flex", alignItems: "center", gap: 7 }}>
          <span>{icon}</span>{label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>
          {count} <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>({pct}%)</span>
        </span>
      </div>
      <div style={{ height: 8, background: "var(--border)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`, background: color,
          borderRadius: 999, transition: "width 0.6s ease",
        }} />
      </div>
    </div>
  );
}

function StepRow({ step, count, max }: { step: string; count: number; max: number }): React.ReactElement {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  const label = STEP_LABELS[step] ?? step.replace(/_/g, " ");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 12, color: "var(--muted)", width: 180, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: "var(--border)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "var(--brand)", borderRadius: 999, transition: "width 0.4s ease" }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", width: 28, textAlign: "right" }}>{count}</span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export function MetricsDashboardPage(): React.ReactElement {
  const [data, setData] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const load = useCallback(() => {
    setLoading((prev) => { if (!prev) setLoading(true); return true; });
    /** Scoped by JWT `institution_id` on the server — do not pass user `sub` as lender_id. */
    void api.cases.metrics()
      .then((res) => { setData(res.data); setLastRefreshed(new Date()); setError(null); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load metrics"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  const totalActive = data?.summary.total_active ?? 0;
  const maxStepCount = Math.max(...(data?.step_distribution.map((s) => s.count) ?? [1]), 1);

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: "var(--ink)" }}>Metrics Dashboard</h1>
          <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: 14 }}>
            Portfolio overview — throughput, recoveries, and pipeline stage distribution.
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <button
            onClick={load}
            disabled={loading}
            aria-label="Refresh metrics"
            style={{ padding: "9px 18px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "var(--panel)", color: "var(--brand)", fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "↻ Refreshing…" : "↻ Refresh"}
          </button>
          {lastRefreshed && (
            <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--muted)" }}>
              Last updated: {lastRefreshed.toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {error && (
        <div role="alert" style={{ background: "var(--danger-bg)", color: "var(--danger)", borderRadius: "var(--radius-sm)", padding: "12px 16px", marginBottom: 20, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Summary stat cards */}
      {loading && !data ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16, marginBottom: 28 }}>
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <div key={n} style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "20px 22px", height: 110, animation: "pulse 1.4s ease-in-out infinite" }} />
          ))}
        </div>
      ) : data ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16, marginBottom: 28 }}>
          <StatCard icon="📂" label="Total Cases" value={data.summary.total_all} accent="var(--brand)" />
          <StatCard icon="⚡" label="Active Cases" value={data.summary.total_active} accent="#6366f1" />
          <StatCard icon="✅" label="Recovered" value={data.summary.total_closed} sub="Total closed cases" accent="#10b981" />
          <StatCard icon="📅" label="Recovered (30d)" value={data.summary.recovered_last_30d} sub="Last 30 days" accent="#3b82f6" />
          <StatCard icon="⚠️" label="Stalled" value={data.summary.stalled} sub="> 7 days in initiation" accent="#f59e0b" />
          <StatCard
            icon="📊"
            label="Recovery Rate"
            value={`${data.summary.recovery_rate}%`}
            sub={`${data.summary.total_closed} of ${data.summary.total_all} cases`}
            accent={data.summary.recovery_rate >= 70 ? "#10b981" : data.summary.recovery_rate >= 40 ? "#f59e0b" : "#ef4444"}
          />
        </div>
      ) : null}

      {/* Two-column layout: stage pipeline + step distribution */}
      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

          {/* Pipeline Stage Breakdown */}
          <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "22px 24px", boxShadow: "var(--shadow)" }}>
            <h2 style={{ margin: "0 0 20px", fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink)" }}>
              Active Cases by Stage
            </h2>
            {STAGE_META.map((s) => (
              <StageBar
                key={s.key}
                label={s.label}
                icon={s.icon}
                count={data.by_stage[s.key] ?? 0}
                total={totalActive > 0 ? totalActive : 1}
                color={s.color}
              />
            ))}
            {totalActive === 0 && (
              <p style={{ color: "var(--muted)", fontSize: 14, textAlign: "center", marginTop: 20 }}>No active cases in the pipeline.</p>
            )}
          </div>

          {/* Step Distribution Table */}
          <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "22px 24px", boxShadow: "var(--shadow)" }}>
            <h2 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink)" }}>
              Step Distribution (All Cases)
            </h2>
            <div style={{ overflowY: "auto", maxHeight: 400 }}>
              {data.step_distribution.length === 0 ? (
                <p style={{ color: "var(--muted)", fontSize: 14 }}>No cases found.</p>
              ) : (
                data.step_distribution.map((s) => (
                  <StepRow key={s.step} step={s.step} count={s.count} max={maxStepCount} />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recovery rate callout */}
      {data && data.summary.stalled > 0 && (
        <div style={{
          marginTop: 20, background: "#fef3c7", border: "1px solid #fde68a",
          borderRadius: "var(--radius-sm)", padding: "14px 18px", display: "flex", gap: 12, alignItems: "center",
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#92400e" }}>
              {data.summary.stalled} case{data.summary.stalled !== 1 ? "s" : ""} stalled in Initiation stage
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#b45309" }}>
              These cases have been in the pre-auction initiation stages for more than 7 days. Review them in the Case Queue.
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
      `}</style>
    </>
  );
}
